import { PostgrestError, SupabaseClient } from "@supabase/supabase-js";
import { SearchEvents, trackEvent } from "@/analytics";

import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import { SpecialRow } from "./specials";
import { logException } from "@/analytics/sentry";
import dayjs from "@punchup/utils/dayjs";

export interface VenueSearchResult {
  id: string;
  name: string;
  slug: string;
  city_state: string;
  logo_img: string | null;
  has_punchup_ticketing: boolean;
}

export interface CombinedSearchResult {
  comedians: Comedian[];
  specials: SpecialRow[];
  venues: VenueSearchResult[];
}

function sanitizeSearchTerm(searchTerm: string): string {
  return searchTerm.trim().toLowerCase().replaceAll(" ", "+");
}

export async function findMatchingComedians(
  supabase: SupabaseClient<Database>,
  searchTerm: string
): Promise<Comedian[]> {
  if (!searchTerm) {
    return [];
  }
  const { data } = await supabase
    .rpc("search_comedians_fts_prefix", {
      prefix: sanitizeSearchTerm(searchTerm),
    })
    .throwOnError();
  return data ?? [];
}

export async function findMatchingSpecials(
  supabase: SupabaseClient<Database>,
  searchTerm: string
): Promise<SpecialRow[]> {
  if (!searchTerm) {
    return [];
  }
  const { data } = await supabase
    .rpc("search_specials_fts_prefix", {
      prefix: sanitizeSearchTerm(searchTerm),
    })
    .throwOnError();
  return data ?? [];
}

export async function findMatchingVenues(
  supabase: SupabaseClient<Database>,
  searchTerm: string
): Promise<VenueSearchResult[]> {
  if (!searchTerm) {
    return [];
  }
  // Escape PostgREST ilike metacharacters and filter syntax to prevent injection
  const sanitized = searchTerm
    .trim()
    .replace(/[%_\\]/g, "\\$&")
    .replace(/[.,()]/g, "");
  const { data: venuePages } = await supabase
    .from("venue_pages")
    .select("id, name, slug, city_state, logo_img, location_ids")
    .eq("is_live", true)
    .eq("is_producer", false)
    .not("published_at", "is", null)
    .or(`name.ilike.%${sanitized}%,city_state.ilike.%${sanitized}%`)
    .order("name")
    .limit(10)
    .throwOnError();

  if (!venuePages || venuePages.length === 0) return [];

  // Collect all location_ids across matched venues to batch-check ticketing
  // Only count venues with upcoming Punchup-ticketed shows as verified
  const allLocationIds = venuePages.flatMap((v) => v.location_ids ?? []);
  const venueIdsWithTicketing = new Set<string>();

  if (allLocationIds.length > 0) {
    const { data: ticketedShows } = await supabase
      .from("comedian_shows")
      .select("venue_id")
      .in("venue_id", allLocationIds)
      .not("tixologi_event_id", "is", null)
      .gte("datetime", dayjs().toISOString())
      .limit(100);

    if (ticketedShows) {
      ticketedShows.forEach((show) => venueIdsWithTicketing.add(show.venue_id));
    }
  }

  return venuePages.map((v) => ({
    id: v.id,
    name: v.name,
    slug: v.slug,
    city_state: v.city_state,
    logo_img: v.logo_img,
    has_punchup_ticketing: (v.location_ids ?? []).some((lid) =>
      venueIdsWithTicketing.has(lid)
    ),
  }));
}

export async function findMatchingComediansAndSpecials(
  supabase: SupabaseClient<Database>,
  searchTerm: string
): Promise<CombinedSearchResult> {
  try {
    const [comedians, specials] = await Promise.all([
      findMatchingComedians(supabase, searchTerm),
      findMatchingSpecials(supabase, searchTerm),
    ]);
    return { comedians, specials, venues: [] };
  } catch (error: unknown) {
    const dberror = error as PostgrestError;
    logException(error, {
      extra: {
        searchTerm,
      },
      tags: {
        supabase: true,
      },
    });
    trackEvent(SearchEvents.SEARCH_QUERY_ERROR, {
      searchTerm,
      error: dberror.message,
    });
    return { comedians: [], specials: [], venues: [] };
  }
}

export async function findMatchingAll(
  supabase: SupabaseClient<Database>,
  searchTerm: string
): Promise<CombinedSearchResult> {
  try {
    const [comedians, specials, venues] = await Promise.all([
      findMatchingComedians(supabase, searchTerm),
      findMatchingSpecials(supabase, searchTerm),
      findMatchingVenues(supabase, searchTerm),
    ]);
    return { comedians, specials, venues };
  } catch (error: unknown) {
    const dberror = error as PostgrestError;
    logException(error, {
      extra: { searchTerm },
      tags: { supabase: true },
    });
    trackEvent(SearchEvents.SEARCH_QUERY_ERROR, {
      searchTerm,
      error: dberror.message,
    });
    return { comedians: [], specials: [], venues: [] };
  }
}
